/**
 * Recover WKWebView tap handling after native StoreKit / Browser / modal flows.
 */

'use client'

import {
  describeTapProbeElement,
  logCapacitorTapProbeTarget,
  type TapProbeTargetId,
} from '@/lib/debug/capacitorTapProbe'
import { dismissCapacitorNativeSplashOverlays } from '@/lib/pwa/dismissCapacitorNativeSplash'
import { recoverNativeTouchHandling } from '@/lib/native/nativeTouchRecovery'

export const NATIVE_PAYMENT_IN_PROGRESS_KEY = 'wagamour_native_payment_in_progress'

const OVERLAY_SELECTOR =
  '#pwa-splash, [data-wagamour-blocker="true"], .wagamour-payment-blocker'

function readRootStyle(el: HTMLElement | null): Record<string, string> | null {
  if (!el) return null
  const style = window.getComputedStyle(el)
  return {
    pointerEvents: style.pointerEvents,
    overflow: style.overflow,
    touchAction: style.touchAction,
    zIndex: style.zIndex,
  }
}

export function setNativePaymentInProgress(active: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (active) {
      sessionStorage.setItem(NATIVE_PAYMENT_IN_PROGRESS_KEY, String(Date.now()))
    } else {
      sessionStorage.removeItem(NATIVE_PAYMENT_IN_PROGRESS_KEY)
    }
  } catch {
    /* ignore */
  }
}

export function isNativePaymentInProgress(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return sessionStorage.getItem(NATIVE_PAYMENT_IN_PROGRESS_KEY) != null
  } catch {
    return false
  }
}

const COLD_START_RECOVERY_REASONS = new Set([
  'visibilitychange_visible',
  'app_state_active',
])

/** Remove stuck pointer/scroll locks and native overlay blockers after IAP/Browser flows. */
export function forceClearNativeInteractionLocks(reason: string): void {
  if (typeof window === 'undefined') return

  const isColdStartRecovery = COLD_START_RECOVERY_REASONS.has(reason)
  const paymentInProgress = isNativePaymentInProgress()

  const html = document.documentElement
  const body = document.body

  for (const el of [html, body]) {
    el.style.removeProperty('pointer-events')
    el.style.removeProperty('overflow')
    el.style.removeProperty('touch-action')
    el.style.removeProperty('padding-right')
    el.removeAttribute('inert')
    el.removeAttribute('aria-hidden')
  }
  // Do not strip body position/top here — that races modal unlockDocumentScroll
  // and can jump the page to y=0 before scroll is restored.

  const innerShell = document.querySelector<HTMLElement>('.app-beta-inner-shell')
  if (innerShell) {
    innerShell.style.pointerEvents = 'auto'
    innerShell.style.removeProperty('touch-action')
  }

  const fixedShell = document.querySelector<HTMLElement>('.app-beta-fixed-shell')
  if (fixedShell && !(isColdStartRecovery && !paymentInProgress)) {
    // Shared document-scroll shell is always interactive (no fixed overlay trap).
    fixedShell.style.pointerEvents = 'auto'
    fixedShell.style.touchAction = 'pan-y'
  }

  document.querySelectorAll(OVERLAY_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement)) return
    node.style.setProperty('pointer-events', 'none', 'important')
    node.style.setProperty('display', 'none', 'important')
    node.style.setProperty('visibility', 'hidden', 'important')
    node.setAttribute('aria-hidden', 'true')
    if (node.id === 'pwa-splash') {
      node.remove()
    }
  })

  if (!isColdStartRecovery || paymentInProgress) {
    setNativePaymentInProgress(false)
  }

  if (!isColdStartRecovery || paymentInProgress) {
    dismissCapacitorNativeSplashOverlays()
  }

  if (!isColdStartRecovery || paymentInProgress) {
    void recoverNativeTouchHandling(reason)
  }

  console.log(
    '[UpgradeTrace9419bd7] force_clear_native_interaction_locks ' +
      JSON.stringify({
        reason,
        paymentInProgress: isNativePaymentInProgress(),
        body: readRootStyle(body),
        html: readRootStyle(html),
        innerShell: readRootStyle(innerShell),
      })
  )
}

function probeTarget(targetId: TapProbeTargetId): Record<string, unknown> {
  const selector = `[data-tap-probe="${targetId}"]`
  const expected =
    document.querySelector<HTMLElement>(selector) ??
    (targetId === 'profile-tab'
      ? (document
          .querySelector('#bottom-navigation nav[aria-label="Main tabs"] button:last-child') as
          | HTMLElement
          | null)
      : (document.querySelector(
          '.bg-amber-50.border-b.border-amber-200 button[data-tap-probe="guest-apple-cta"]'
        ) as HTMLElement | null))

  if (!expected) {
    return { targetId, found: false }
  }

  const rect = expected.getBoundingClientRect()
  const x = Math.round(rect.left + rect.width / 2)
  const y = Math.round(rect.top + rect.height / 2)
  const hitEl = document.elementFromPoint(x, y)

  return {
    targetId,
    probePoint: { x, y },
    expected: describeTapProbeElement(expected),
    elementFromPoint: describeTapProbeElement(hitEl),
    interceptsTap: !(hitEl === expected || (hitEl != null && expected.contains(hitEl))),
  }
}

/** Log hit-test state for guest Apple CTA + bottom nav after payment cancel. */
export function logPostCancelTapState(context: string): void {
  if (typeof window === 'undefined') return

  const html = document.documentElement
  const body = document.body
  const innerShell = document.querySelector<HTMLElement>('.app-beta-inner-shell')

  const payload = {
    context,
    href: window.location.href,
    body: readRootStyle(body),
    html: readRootStyle(html),
    innerShell: readRootStyle(innerShell),
    paymentInProgress: isNativePaymentInProgress(),
    guestAppleCta: probeTarget('guest-apple-cta'),
    bottomNavProfile: probeTarget('profile-tab'),
  }

  console.log(
    '[UpgradeTrace9419bd7] post_cancel_tap_state ' + JSON.stringify(payload)
  )

  logCapacitorTapProbeTarget('guest-apple-cta')
  logCapacitorTapProbeTarget('profile-tab')
}
